<script>
import { ThreadCategoryStatusEnum, ThreadStatusEnum } from "@/utils/constants";
const PADDING_SIZE = Object.freeze({
  NORMAL: "normal",
  BIG: "big",
});
const threadStatusThemeMap = Object.freeze({
    [ThreadStatusEnum.AI_ONGOING]: "green",
    [ThreadStatusEnum.NEW]: "red",
    [ThreadStatusEnum.OPEN]: "yellow",
    [ThreadStatusEnum.PENDING_USER_REPLY]: "yellow",
    [ThreadStatusEnum.ONHOLD]: "gray",
    [ThreadStatusEnum.PENDING_FORM_SUBMISSION]: "red",
    [ThreadStatusEnum.USER_CLOSED]: "green",
    [ThreadStatusEnum.SUPPORT_CLOSED]: "green",
    [ThreadStatusEnum.AUTO_CLOSED]: "green",
    [ThreadStatusEnum.SOFT_ALERT]: "yellow",
    [ThreadStatusEnum.AI_CLOSED]: "green",
    [ThreadStatusEnum.ESCALATED]: "red",
    [ThreadStatusEnum.REOPENED]: "red",
    [ThreadCategoryStatusEnum.TICKETS]: "red",
})

// given the status we need to have mappings to its label, and colour
const threadStatusLabelMap = Object.freeze({
    [ThreadStatusEnum.AI_ONGOING]: "AI Ongoing",
    [ThreadStatusEnum.NEW]: "New",
    [ThreadStatusEnum.ONGOING]: "Ongoing",
    [ThreadStatusEnum.OPEN]: "Open",
    [ThreadStatusEnum.PENDING_USER_REPLY]: "Pending User Reply",
    [ThreadStatusEnum.ONHOLD]: "On Hold",
    [ThreadStatusEnum.PENDING_FORM_SUBMISSION]: "Pending Form Submission",
    [ThreadStatusEnum.USER_CLOSED]: "User Closed",
    [ThreadStatusEnum.SUPPORT_CLOSED]: "Support Closed",
    [ThreadStatusEnum.AUTO_CLOSED]: "Auto Closed",
    [ThreadStatusEnum.SOFT_ALERT]: "Soft Alert",
    [ThreadStatusEnum.AI_CLOSED]: "AI Closed",
    [ThreadStatusEnum.ESCALATED]: "Escalated",
    [ThreadStatusEnum.REOPENED]: "Reopened",
    [ThreadCategoryStatusEnum.TICKETS]: "Tickets",
})
</script>

<script setup>
import { computed } from "vue";
import { threadStatusIconHollowMap } from "./ThreadStatusDropdown2.vue";

const emit = defineEmits(["toggle"]);

const props = defineProps({
  value: {
    type: [String, Number],
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  count: {
    type: [String, Number],
    default: null,
  },
  paddingSize: {
    type: String,
    validator: (v) => Object.values(PADDING_SIZE).includes(v),
    default: PADDING_SIZE.NORMAL,
  },
  darker: {
    type: Boolean,
    default: false,
  },
});

const paddingClass = computed(() =>
  props.paddingSize === "big" ? "padding-big" : "padding-normal"
);

const theme = computed(() => 
    props.value ? threadStatusThemeMap[props.value] : null
)

const label = computed(() =>  props.value ? threadStatusLabelMap[props.value] : "No label")

const isHollow = computed(() => threadStatusIconHollowMap[props.value] || false)

const toggle = () => {
  emit("toggle", props.value);
};
</script>

<template>
  <span
    class="status-badge text-neutral-400"
    :class="{
      'status-badge--selected': selected,
      'status-badge--darker': darker,
      [paddingClass]: true,
      [theme]: theme,
    }"
    @click="toggle"
  >
    <span class="d-flex align-items-center">
      <IconCircleFill :isHollow="isHollow" />
      <span class="ms-2 font-size-xs text-neutral-400">
        {{ label }}
        <template v-if="count">({{ count }})</template>
      </span>
    </span>
  </span>
</template>

<style scoped lang="scss">
.status-badge {
  border: 1px solid $bluish-gray-300;
  border-radius: 60px;
  font-size: 0.875rem;
  stroke-width: 3px;
  cursor: pointer;

  &.padding-normal {
    padding: 0.25rem 0.5rem;
  }

  &.padding-big {
    padding: 0.5rem 0.75rem;
  }

  &.status-badge--selected {
    &.red {
      background-color: $red-50;
      border: 1px solid $red-100;
    }
    &.gray {
      background-color: $neutral-150;
      border: 1px solid $neutral-300;
    }
    &.green {
      background-color: $green-50;
      border: 1px solid $green-100;
    }
    &.yellow {
      background-color: $yellow-50;
      border: 1px solid $yellow-100;
    }
    &.blue {
      background-color: var(--primary-50);
      border: 1px solid var(--primary-150);
    }
  }

  &.status-badge--darker {
    &.red {
      background-color: $red-0;
      border: 1px solid $red-100;
    }
    &.gray {
      background-color: $neutral-50;
      border: 1px solid $neutral-200;
    }
    &.green {
      background-color: $green-0;
      border: 1px solid $green-100;
    }
  }

  &.red {
    background-color: $red-0;
    border: 1px solid $red-50;
    svg {
      stroke: $red-200;
      fill: $red-200;
    }
  }
  &.green {
    background-color: $green-0;
    border: 1px solid $green-50;
    svg {
      stroke: $green-150;
      fill: $green-150;
    }
  }
  &.yellow {
    background-color: $yellow-0;
    border: 1px solid $yellow-50;
    svg {
      stroke: $yellow-250;
      fill: $yellow-250;
    }
  }
  &.blue {
    background-color: var(--primary-0);
    border: 1px solid var(--primary-100);
    svg {
      stroke: var(--primary-250);
      fill: var(--primary-250);
    }
  }
  &.gray {
    background-color: $neutral-50;
    border: 1px solid $neutral-150;
    svg {
      stroke: $neutral-250;
      fill: $neutral-250;
    }
  }
}
</style>
